import type { AuctionEngine } from '../game/engine';
import type { Offset } from '../game/geom';
import type { RoundSetup } from '../game/themes';
import type { ThemeVisual } from '../game/visual';
import type { T } from '../i18n/strings';
import { BuildCanvas } from '../components/BuildCanvas';
import { Button, PLAY_HOST, useCountdown, VoteBars } from '../components/ui';
import type { Tally } from '../stream/useVote';
import { PLAYER_COLORS } from '../theme';

export interface Decision {
  by: 'ai' | 'chat' | 'host';
  winner: number | null; // null = tie
  scores?: number[];
  verdict?: string;
  votes?: number[];
}

interface Props {
  t: T;
  engine: AuctionEngine;
  round: RoundSetup;
  visual: ThemeVisual | null;
  names: string[];
  moved: Map<number, Offset>[];
  onMove: (player: number, itemIndex: number, centre: Offset) => void;
  decision: Decision | null;
  judging: boolean;
  judgeError: string | null;
  aiJudge: boolean;
  chatConnected: boolean;
  chatVoting: boolean;
  chatTally: Tally;
  onAskAi: () => void;
  onAskChat: () => void;
  onCloseChat: () => void;
  onCrown: (player: number) => void;
  onNewGame: () => void;
  onReplay: () => void;
  onSave: () => void;
  onMenu: () => void;
  showLink: boolean;
  toast: string | null;
}

export function Result(p: Props) {
  const { t, engine: e, round, visual, names, decision } = p;
  const big = e.biggestPurchase();
  const winner = decision?.winner ?? null;

  const column = (player: number) => (
    <div className={`res-col ${winner === player ? 'winner' : ''}`} style={{ ['--pc' as string]: PLAYER_COLORS[player] }}>
      <div className="pane-head" style={{ color: PLAYER_COLORS[player] }}>
        <span className="pane-name">
          {winner === player && <span className="crown">👑</span>}
          {names[player]}
        </span>
        <span className="pane-money">
          {decision?.scores ? <span className="score">{decision.scores[player].toFixed(1)}</span> : null}
          {t.t('left', { amount: e.wallets[player] })}
        </span>
      </div>
      <BuildCanvas
        visual={visual}
        won={e.builds[player].map((w) => round.sources[w.itemIndex])}
        names={e.builds[player].map((w) => w.name)}
        moved={p.moved[player]}
        onMove={(i, c) => p.onMove(player, i, c)}
        accent={PLAYER_COLORS[player]}
        className="res-build"
      />
      <ul className="res-items">
        {e.builds[player].length === 0 && <li className="muted">{t.a('twentyChallengeNothingWon')}</li>}
        {e.builds[player].map((w) => (
          <li key={w.itemIndex}>
            {w.name} <b>${w.price}</b>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="result">
      <header className="stage-top">
        <button className="icon-btn" onClick={p.onMenu} aria-label={t.t('menu')}>
          ☰
        </button>
        <div className="stage-title">{round.theme.theme}</div>
        <div />
      </header>
      <div className="res-row">
        {column(0)}
        <div className="verdict">
          <Center {...p} />
          {big && <div className="biggest">{t.t('biggestBuy', { item: big.name, price: big.price })}</div>}
        </div>
        {column(1)}
      </div>
      <div className="res-actions">
        {visual && <span className="drag-hint">{t.a('twentyChallengeDragHint')}</span>}
        <Button kind="secondary" onClick={p.onSave}>
          ⤓ {t.a('twentyChallengeSaveImage')}
        </Button>
        <Button kind="secondary" onClick={p.onReplay}>
          {t.t('againSameTheme')}
        </Button>
        <Button onClick={p.onNewGame} className="big">
          {t.a('twentyChallengeNewGame')}
        </Button>
      </div>
      {p.toast && <div className="toast">{p.toast}</div>}
      {p.showLink && <div className="watermark">{PLAY_HOST}</div>}
    </div>
  );
}

function Center(p: Props) {
  const { t, names, decision } = p;
  const left = useCountdown(p.chatVoting, 30, p.onCloseChat);

  if (p.judging) {
    return (
      <div className="verdict-box">
        <div className="spinner" />
        <p className="verdict-wait">{t.a('twentyChallengeJudging')}</p>
      </div>
    );
  }
  if (p.chatVoting) {
    return (
      <div className="verdict-box">
        <h2>{t.t('chatVote')}</h2>
        <p className="vote-how">{t.t('chatVoteHow', { p1: names[0], p2: names[1] })}</p>
        <VoteBars tally={p.chatTally} labels={names} colors={PLAYER_COLORS} />
        <div className="vote-foot">
          <span className="countdown">{left}s</span>
          <Button kind="secondary" onClick={p.onCloseChat}>
            {t.t('closeVote')}
          </Button>
        </div>
      </div>
    );
  }
  if (decision) {
    const headline = decision.winner === null ? t.t('tie') : t.t('wins', { name: names[decision.winner] });
    return (
      <div className="verdict-box decided">
        <div className="trophy">{decision.winner === null ? '🤝' : '🏆'}</div>
        <h2 style={{ color: decision.winner === null ? undefined : PLAYER_COLORS[decision.winner] }}>{headline}</h2>
        {decision.by === 'ai' && decision.verdict && (
          <>
            <div className="verdict-by">{t.t('aiSays')}</div>
            <blockquote>{decision.verdict}</blockquote>
          </>
        )}
        {decision.by === 'chat' && decision.votes && (
          <>
            <div className="verdict-by">{t.t('chatSays')}</div>
            <div className="chat-result">
              {decision.votes.map((v, i) => (
                <span key={i} style={{ color: PLAYER_COLORS[i] }}>
                  {names[i]}: {v}
                </span>
              ))}
            </div>
          </>
        )}
        <div className="again">
          {p.aiJudge && decision.by !== 'ai' && (
            <Button kind="ghost" onClick={p.onAskAi}>
              {t.a('twentyChallengeLetAiDecide')}
            </Button>
          )}
          {p.chatConnected && decision.by !== 'chat' && (
            <Button kind="ghost" onClick={p.onAskChat}>
              {t.t('askChat')}
            </Button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="verdict-box">
      <h2>{t.a('twentyChallengeWhoWon')}</h2>
      {p.judgeError && <p className="error">{p.judgeError}</p>}
      <div className="who-buttons">
        {p.aiJudge && (
          <Button onClick={p.onAskAi} className="big">
            ✨ {t.a('twentyChallengeLetAiDecide')}
          </Button>
        )}
        {p.chatConnected && (
          <Button kind="secondary" onClick={p.onAskChat} className="big">
            💬 {t.t('askChat')}
          </Button>
        )}
      </div>
      <p className="crown-yourself">{t.a('twentyChallengeDecideYourselves')}</p>
      <div className="crown-buttons">
        {names.map((n, i) => (
          <Button key={i} kind={i === 0 ? 'p1' : 'p2'} onClick={() => p.onCrown(i)}>
            👑 {n}
          </Button>
        ))}
      </div>
    </div>
  );
}
